import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useCourses, useExtractModule, useModules, useRetryFailedExtractions } from "../api/queries";
import { JobNotice } from "../components/JobNotice";
import { PageNav } from "../components/PageNav";
import { Panel } from "../components/Panel";
import { Button, Empty, ErrorBanner, Loading, PageHeader, RelativeTime } from "../components/ui";

const PAGE_SIZE = 50;

// Row-card table: brand header bar over separated rounded white rows.
const TABLE = "w-full border-separate border-spacing-x-0 border-spacing-y-2";
const HEAD_CELL = "bg-brand px-4 py-3 text-left text-xs font-semibold whitespace-nowrap text-white";
const BODY_CELL = "border-y border-gray-100 px-4 py-3 align-middle";

function headCell(pos: "first" | "mid" | "last") {
  return [HEAD_CELL, pos === "first" && "rounded-l-lg", pos === "last" && "rounded-r-lg"].filter(Boolean).join(" ");
}

function bodyCell(pos: "first" | "mid" | "last", selected = false) {
  return [
    BODY_CELL,
    pos === "first" && "rounded-l-lg border-l",
    pos === "last" && "rounded-r-lg border-r",
    selected ? "bg-brand-light" : "bg-white group-hover:bg-gray-50",
  ].filter(Boolean).join(" ");
}

// Modules whose last extraction failed (GET /moodle/modules?failed=true), with
// a per-module retry, a retry for the ticked modules, and a retry for every
// failing module in scope. ?course=<moodle id> narrows it to one course.
export function FailedExtractionsPage() {
  const [params, setParams] = useSearchParams();
  const courseParam = params.get("course");
  const courseMoodleId = courseParam ? Number(courseParam) : undefined;
  const [offset, setOffset] = useState(0);

  const modules = useModules({ failed: true, courseMoodleId, offset, limit: PAGE_SIZE });
  const courses = useCourses();
  const retry = useExtractModule();
  const retryMany = useRetryFailedExtractions();
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const rows = modules.data?.modules ?? [];
  const total = modules.data?.total ?? 0;
  const busy = retry.isPending || retryMany.isPending;
  const allOnPageSelected = rows.length > 0 && rows.every((m) => selected.has(m.moodle_cmid));

  function toggle(cmid: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(cmid)) next.delete(cmid);
      else next.add(cmid);
      return next;
    });
  }

  return (
    <div className="p-6">
      <PageHeader
        title="Failed extractions"
        subtitle="Modules whose content couldn't be extracted the last time it was tried. Fix the cause in Moodle (or the worker), then retry."
        actions={
          <>
            <Button
              disabled={!selected.size || busy}
              loading={retryMany.isPending && !!retryMany.variables?.cmids}
              onClick={() => retryMany.mutate({ cmids: [...selected] }, { onSuccess: () => setSelected(new Set()) })}
            >
              Retry Selected ({selected.size})
            </Button>
            <Button
              variant="brand"
              disabled={!total || busy}
              loading={retryMany.isPending && !retryMany.variables?.cmids}
              onClick={() =>
                retryMany.mutate(
                  courseMoodleId !== undefined ? { courseMoodleIds: [courseMoodleId] } : {},
                  { onSuccess: () => setSelected(new Set()) }
                )
              }
            >
              Retry All Failed
            </Button>
          </>
        }
      />
      <ErrorBanner error={retry.error || retryMany.error} />
      <JobNotice kind="extract" />
      {retry.data && (
        <p className="mb-4 text-sm text-green-700">
          Module re-extracted ({retry.data.status}): {retry.data.documents_written} documents written. New documents are in the approval queue.
        </p>
      )}

      <Panel
        title={modules.data ? `${modules.data.total.toLocaleString()} failing modules` : "Failing modules"}
        actions={
          <select
            aria-label="Course"
            value={courseParam ?? ""}
            onChange={(e) => {
              setOffset(0);
              setSelected(new Set());
              setParams(e.target.value ? { course: e.target.value } : {});
            }}
            className="w-72 max-w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-700 focus:border-brand focus:outline-none"
          >
            <option value="">All courses</option>
            {(courses.data?.courses ?? []).map((c) => (
              <option key={c.moodle_id} value={c.moodle_id}>
                {c.fullname}{c.stats?.extraction_failed ? ` (${c.stats.extraction_failed} failed)` : ""}
              </option>
            ))}
          </select>
        }
      >
        <ErrorBanner error={modules.error} />
        {modules.isLoading ? <Loading /> : !rows.length ? (
          <Empty>No failed extractions{courseMoodleId !== undefined ? " in this course" : ""}.</Empty>
        ) : (
          <div className="overflow-x-auto">
            <table className={TABLE}>
              <thead>
                <tr>
                  <th className={headCell("first")}>
                    <span className="inline-flex items-center gap-2">
                      <input
                        type="checkbox"
                        aria-label="Select page"
                        checked={allOnPageSelected}
                        onChange={() => setSelected(allOnPageSelected ? new Set() : new Set(rows.map((m) => m.moodle_cmid)))}
                      />
                      Document
                    </span>
                  </th>
                  <th className={headCell("mid")}>Course / section</th>
                  <th className={headCell("mid")}>Type</th>
                  <th className={headCell("mid")}>Error</th>
                  <th className={headCell("mid")}>Synced</th>
                  <th className={headCell("last")} />
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => {
                  const sel = selected.has(m.moodle_cmid);
                  return (
                    <tr key={m.moodle_cmid} className="group">
                      <td className={bodyCell("first", sel)}>
                        <span className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            className="mt-0.5"
                            aria-label={`Select ${m.name}`}
                            checked={sel}
                            onChange={() => toggle(m.moodle_cmid)}
                          />
                          <span className="min-w-0">
                            {m.url ? (
                              <a href={m.url} target="_blank" rel="noreferrer" className="font-medium text-gray-900 hover:text-brand hover:underline">{m.name}</a>
                            ) : (
                              <span className="font-medium text-gray-900">{m.name}</span>
                            )}
                            <div className="text-xs text-gray-500">cmid {m.moodle_cmid}</div>
                          </span>
                        </span>
                      </td>
                      <td className={bodyCell("mid", sel)}>
                        {m.course_moodle_id !== null ? (
                          <Link to={`/courses/${m.course_moodle_id}`} className="font-medium text-gray-900 hover:text-brand hover:underline">
                            {m.course_fullname ?? `Course ${m.course_moodle_id}`}
                          </Link>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                        {m.section_name && (
                          <div className="text-xs text-gray-500">
                            {m.section_number !== null && `${m.section_number}. `}{m.section_name}
                          </div>
                        )}
                      </td>
                      <td className={bodyCell("mid", sel)}>
                        <code className="text-xs text-gray-700">{m.modname}</code>
                      </td>
                      <td className={`${bodyCell("mid", sel)} max-w-md break-words text-xs text-red-700`}>{m.extraction_error}</td>
                      <td className={`${bodyCell("mid", sel)} whitespace-nowrap text-gray-600`}>
                        <RelativeTime iso={m.synced_at} />
                      </td>
                      <td className={`${bodyCell("last", sel)} whitespace-nowrap text-right`}>
                        <Button
                          loading={retry.isPending && retry.variables === m.moodle_cmid}
                          disabled={busy}
                          onClick={() => retry.mutate(m.moodle_cmid)}
                        >
                          Retry
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {!!rows.length && (
          <PageNav
            offset={offset}
            limit={PAGE_SIZE}
            total={modules.data?.total}
            onChange={(o) => {
              setOffset(o);
              setSelected(new Set());
            }}
          />
        )}
      </Panel>
    </div>
  );
}
